/**
 * Isometric projection helpers for the Virtual Office.
 *
 * We keep world coordinates in the familiar Cartesian space (x = 0..MAP_W,
 * y = 0..MAP_H) so backend rooms and presence broadcasts don't need any
 * changes. Rendering converts world → screen with a 2:1-ish iso projection
 * tuned so a 1000×640 world becomes roughly a 1400×820 iso canvas.
 */
export const ISO_SCALE_X = 0.85;
export const ISO_SCALE_Y = 0.5;
// A "tile" is 1 world-unit; visible extent in screen: ~1.7 px wide × 1 px tall.
// Room walls are drawn WALL_HEIGHT_PX tall (visual depth).
export const WALL_HEIGHT_PX = 28;

/**
 * Project world (x, y) → screen (sx, sy).
 * `offsetX` is added so world y-axis maxes fit inside the canvas.
 */
export function worldToScreen(x, y, offsetX = 0) {
  return {
    sx: (x - y) * ISO_SCALE_X + offsetX,
    sy: (x + y) * ISO_SCALE_Y,
  };
}

/** Screen (sx, sy) → world (x, y). Used for click-to-move. */
export function screenToWorld(sx, sy, offsetX = 0) {
  const ax = sx - offsetX;
  const ay = sy;
  const x = (ax / ISO_SCALE_X + ay / ISO_SCALE_Y) / 2;
  const y = (ay / ISO_SCALE_Y - ax / ISO_SCALE_X) / 2;
  return { x, y };
}

/**
 * Compute the iso canvas dimensions for a world of (worldW × worldH).
 * Returns {canvasW, canvasH, offsetX}. Add ~24px vertical headroom so
 * wall depth + avatar name labels never clip.
 */
export function isoCanvasFor(worldW, worldH) {
  const offsetX = worldH * ISO_SCALE_X;
  const canvasW = (worldW + worldH) * ISO_SCALE_X;
  const canvasH = (worldW + worldH) * ISO_SCALE_Y + WALL_HEIGHT_PX + 40;
  return { canvasW, canvasH, offsetX };
}

/**
 * Rotate a screen-aligned WASD input into world axes so W walks UP on
 * the iso canvas. Derivation: given the iso projection
 *   dsx = (dx - dy) * SX
 *   dsy = (dx + dy) * SY
 * and the desired screen directions
 *   D → (dsx=+, dsy=0)   A → (dsx=-, dsy=0)
 *   W → (dsx=0, dsy=-)   S → (dsx=0, dsy=+)
 * the required world deltas are:
 *   D → (+1, -1) NE      A → (-1, +1) SW
 *   W → (-1, -1) NW      S → (+1, +1) SE
 * which comes from wx = dx + dy, wy = dy - dx.
 */
export function rotateInputForIso(dx, dy) {
  return { wx: dx + dy, wy: dy - dx };
}

// ---------------------------------------------------------------------------
// Wall collision helpers
// ---------------------------------------------------------------------------

/** Axis-aligned point-in-rect (inclusive). */
export function pointInRect(x, y, r) {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

/**
 * Given a room with an optional `door` = {side: "N"|"S"|"E"|"W", width}
 * (offset defaults to center of that wall), return true if world (x, y)
 * is within a thin strip on the door's wall — mainly used for UI hints
 * ("you're near a door") rather than for collision itself.
 */
export function isInDoor(x, y, r) {
  const d = r.door || { side: "S", width: 80 };
  const w = d.width || 80;
  if (d.side === "N" || d.side === "S") {
    const cx = d.cx != null ? d.cx : r.x + r.w / 2;
    const wallY = d.side === "N" ? r.y : r.y + r.h;
    return Math.abs(y - wallY) <= 20 && x >= cx - w / 2 && x <= cx + w / 2;
  }
  const cy = d.cy != null ? d.cy : r.y + r.h / 2;
  const wallX = d.side === "W" ? r.x : r.x + r.w;
  return Math.abs(x - wallX) <= 20 && y >= cy - w / 2 && y <= cy + w / 2;
}

/**
 * Does the line segment (px, py) → (nx, ny) cross the door opening on
 * room `r`? Uses parametric line-segment intersection so we correctly
 * accept movements that step over the door in a single frame even when
 * neither endpoint sits within a "close to the wall" strip.
 */
export function segmentCrossesDoor(px, py, nx, ny, r) {
  const d = r.door || { side: "S", width: 80 };
  const w = d.width || 80;
  if (d.side === "S" || d.side === "N") {
    const wallY = d.side === "N" ? r.y : r.y + r.h;
    const cx = d.cx != null ? d.cx : r.x + r.w / 2;
    // Segment must actually cross y = wallY (both sides).
    if ((py - wallY) * (ny - wallY) > 0) return false;
    if (py === ny) return false;
    const t = (wallY - py) / (ny - py);
    const crossX = px + t * (nx - px);
    return crossX >= cx - w / 2 && crossX <= cx + w / 2;
  }
  const wallX = d.side === "W" ? r.x : r.x + r.w;
  const cy = d.cy != null ? d.cy : r.y + r.h / 2;
  if ((px - wallX) * (nx - wallX) > 0) return false;
  if (px === nx) return false;
  const t = (wallX - px) / (nx - px);
  const crossY = py + t * (ny - py);
  return crossY >= cy - w / 2 && crossY <= cy + w / 2;
}

/**
 * Test whether a step from (px, py) to (nx, ny) crosses a room wall
 * illegally (i.e. not through the door). Returns `null` when legal or
 * the offending room otherwise.
 */
export function findWallCollision(px, py, nx, ny, rooms) {
  const wasIn = rooms.find((r) => pointInRect(px, py, r));
  const nowIn = rooms.find((r) => pointInRect(nx, ny, r));
  if (wasIn && nowIn && wasIn.id === nowIn.id) return null;
  if (!wasIn && !nowIn) return null;
  if (wasIn && !nowIn) {
    return segmentCrossesDoor(px, py, nx, ny, wasIn) ? null : wasIn;
  }
  if (!wasIn && nowIn) {
    return segmentCrossesDoor(px, py, nx, ny, nowIn) ? null : nowIn;
  }
  if (wasIn && nowIn && wasIn.id !== nowIn.id) {
    const exitOk = segmentCrossesDoor(px, py, nx, ny, wasIn);
    const enterOk = segmentCrossesDoor(px, py, nx, ny, nowIn);
    return exitOk && enterOk ? null : nowIn;
  }
  return null;
}
